import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

// ===== Interfaces =====

export interface NotificationUser {
  name: string;
  email: string;
  active: boolean;
}

export interface Notification {
  id: number;
  description: string;
  read: boolean;
  toUserId: number;
  toUser?: NotificationUser;
  createdBy?: string;
  createdAt?: Date;
}

export interface CreateNotificationRequest {
  description: string;
  toUserId: number;
  createdBy: string;
}

export interface MarkAsReadRequest {
  updatedBy: string;
}

export interface NotificationListResponse {
  data: Notification[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface UnreadCountResponse {
  userId: number;
  unreadCount: number;
}

export interface DeleteAllReadResponse {
  message: string;
  userId: number;
  deletedCount: number;
  deletedAt: Date;
}

export interface MessageResponse {
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private baseUrl = environment.microservices.user;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== Criação =====

  /**
   * Cria uma nova notificação para um usuário
   */
  create(request: CreateNotificationRequest): Observable<Notification> {
    return this.http.post<any>(`${this.baseUrl}/CreateNotification`, request).pipe(
      map(dto => this.mapFromDTO(dto)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao criar notificação');
        return throwError(() => err);
      })
    );
  }

  // ===== Consultas =====

  /**
   * Retorna lista paginada de notificações de um usuário
   */
  getByUserId(userId: number, page: number = 1, pageSize: number = 50): Observable<NotificationListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/GetNotifications/${userId}`, { params }).pipe(
      map(response => ({
        data: (response.data || []).map((dto: any) => this.mapFromDTO(dto)),
        pagination: response.pagination
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar notificações');
        return throwError(() => err);
      })
    );
  }

  /**
   * Retorna notificações não lidas de um usuário
   */
  getUnread(userId: number): Observable<Notification[]> {
    return this.http.get<any[]>(`${this.baseUrl}/GetUnreadNotifications/${userId}`).pipe(
      map(notifications => notifications.map(dto => this.mapFromDTO(dto))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar notificações não lidas');
        return throwError(() => err);
      })
    );
  }

  /**
   * Retorna a contagem de notificações não lidas de um usuário
   * Utiliza o endpoint GetUnreadNotifications e calcula o total a partir do array retornado
   */
  getUnreadCount(userId: number): Observable<UnreadCountResponse> {
    return this.http.get<any[]>(`${this.baseUrl}/GetUnreadNotifications/${userId}`).pipe(
      map(notifications => ({
        userId: userId,
        unreadCount: Array.isArray(notifications) ? notifications.length : 0
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar contagem de notificações não lidas');
        return throwError(() => err);
      })
    );
  }

  /**
   * Retorna detalhes de uma notificação específica
   */
  getById(notificationId: number): Observable<Notification> {
    return this.http.get<any>(`${this.baseUrl}/GetNotification/${notificationId}`).pipe(
      map(dto => this.mapFromDTO(dto)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar notificação');
        return throwError(() => err);
      })
    );
  }

  // ===== Marcar como Lida =====

  /**
   * Marca uma notificação específica como lida
   */
  markAsRead(notificationId: number, updatedBy: string): Observable<MessageResponse> {
    const body: MarkAsReadRequest = { updatedBy };
    return this.http.put<MessageResponse>(`${this.baseUrl}/MarkAsRead/${notificationId}`, body).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao marcar notificação como lida');
        return throwError(() => err);
      })
    );
  }

  /**
   * Marca todas as notificações de um usuário como lidas
   */
  markAllAsRead(userId: number, updatedBy: string): Observable<MessageResponse> {
    const body: MarkAsReadRequest = { updatedBy };
    return this.http.put<MessageResponse>(`${this.baseUrl}/MarkAllAsRead/${userId}`, body).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao marcar todas as notificações como lidas');
        return throwError(() => err);
      })
    );
  }

  // ===== Exclusão =====

  /**
   * Realiza soft delete de uma notificação
   */
  delete(notificationId: number, deletedBy: string): Observable<MessageResponse> {
    const body = { deletedBy };
    return this.http.request<MessageResponse>('delete', `${this.baseUrl}/DeleteNotification/${notificationId}`, { body }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao deletar notificação');
        return throwError(() => err);
      })
    );
  }

  /**
   * Deleta todas as notificações lidas de um usuário
   */
  deleteAllRead(userId: number, deletedBy: string): Observable<DeleteAllReadResponse> {
    const body = { deletedBy };
    return this.http.request<DeleteAllReadResponse>('delete', `${this.baseUrl}/DeleteAllRead/${userId}`, { body }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao deletar notificações lidas');
        return throwError(() => err);
      })
    );
  }

  // ===== Mapeamento de Dados =====

  /**
   * Mapeia DTO da API para o modelo Notification
   */
  private mapFromDTO(dto: any): Notification {
    return {
      id: dto.Id || dto.id,
      description: dto.Description || dto.description || '',
      read: dto.Read !== undefined ? dto.Read : (dto.read !== undefined ? dto.read : false),
      toUserId: dto.ToUserId || dto.toUserId,
      toUser: dto.ToUser || dto.toUser ? {
        name: (dto.ToUser || dto.toUser)?.Name || (dto.ToUser || dto.toUser)?.name || '',
        email: (dto.ToUser || dto.toUser)?.Email || (dto.ToUser || dto.toUser)?.email || '',
        active: (dto.ToUser || dto.toUser)?.Active !== undefined
          ? (dto.ToUser || dto.toUser).Active
          : (dto.ToUser || dto.toUser)?.active !== undefined
            ? (dto.ToUser || dto.toUser).active
            : true
      } : undefined,
      createdBy: dto.CreatedBy || dto.createdBy,
      createdAt: dto.CreatedAt || dto.createdAt ? new Date(dto.CreatedAt || dto.createdAt) : undefined
    };
  }
}
